import { useState, useEffect } from 'react';
import {
  NICE_CLASS_OPTIONS,
  type NiceClass,
  type TrademarkSearchResult,
  type TrademarkHit,
  type TrademarkRiskVerdict,
} from '../../shared/types';
import {
  searchTrademark,
  getTrademarkStatus,
  type TrademarkProviderStatus,
} from '../services/api';

interface Props {
  /** Pre-fill the search with the brand_name being typed in the analysis form. */
  initialBrandName: string;
}

export default function TrademarkAvailabilitySection({ initialBrandName }: Props) {
  const [open, setOpen] = useState(false);
  const [brandName, setBrandName] = useState(initialBrandName);
  const [classes, setClasses] = useState<NiceClass[]>([32, 33]);
  const [status, setStatus] = useState<TrademarkProviderStatus | null>(null);
  const [result, setResult] = useState<TrademarkSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resync when the parent input changes — until the user has manually edited
  // the local field. We track that with a "touched" sentinel.
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched) setBrandName(initialBrandName);
  }, [initialBrandName, touched]);

  // Lazy-load provider status the first time the panel opens.
  useEffect(() => {
    if (!open || status) return;
    getTrademarkStatus().then(setStatus).catch(() => setStatus(null));
  }, [open, status]);

  const toggleClass = (c: NiceClass) => {
    setClasses((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const canSearch = brandName.trim().length > 0 && classes.length > 0 && !loading;

  const onSearch = async () => {
    if (!canSearch) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await searchTrademark(brandName.trim(), classes);
      setResult(r);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-amber-300/60 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-900/10 shader:bg-white/5 shader:border-amber-400/20 overflow-hidden">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-amber-100/40 dark:hover:bg-amber-900/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-base">
            🛡️
          </span>
          <div>
            <div className="text-sm font-medium text-slate-900 dark:text-white shader:text-white flex items-center gap-2">
              Vérifier la disponibilité du nom (INPI + EUIPO)
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-600 text-white">
                Admin
              </span>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/60">
              Pré-screen marque sur les bases françaises et européennes — pas un avis CPI.
            </div>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-amber-200/60 dark:border-amber-500/20">
          {/* Provider status */}
          {status && (
            <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <ProviderBadge label="INPI Diffusion PI (FR + EU + WIPO)" ok={status.inpi.configured} message={status.inpi.message} />
              <ProviderBadge label={`EUIPO direct (EUTM en examen — ${status.euipo.env ?? 'sandbox'})`} ok={status.euipo.configured} message={status.euipo.message} />
            </div>
          )}

          {/* Name input (pre-filled, editable) */}
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 shader:text-white/60 mb-1">
            Nom à vérifier
          </label>
          <input
            type="text"
            value={brandName}
            onChange={(e) => {
              setBrandName(e.target.value);
              setTouched(true);
            }}
            placeholder="Saisissez le nom de marque exact"
            className="w-full mb-3 rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/10 bg-white dark:bg-slate-800 shader:bg-white/5 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          />

          {/* Class picker */}
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 shader:text-white/60 mb-1">
            Classes Nice ciblées
            <span className="text-slate-400 font-normal"> — sélection multiple, au moins une</span>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-3">
            {NICE_CLASS_OPTIONS.map((opt) => {
              const checked = classes.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-xs ${
                    checked
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20 shader:bg-violet-500/10'
                      : 'border-slate-200 dark:border-slate-700 shader:border-white/10 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleClass(opt.value)}
                    className="mt-0.5 accent-violet-600"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900 dark:text-white shader:text-white flex items-center gap-1">
                      {opt.label}
                      {opt.beverageRelevant && (
                        <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                          boisson
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 shader:text-white/60 mt-0.5">
                      {opt.description}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Search button */}
          <button
            type="button"
            onClick={onSearch}
            disabled={!canSearch}
            className="w-full py-2.5 rounded-lg text-sm text-white bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Recherche en cours…' : 'Lancer le pré-screen'}
          </button>

          {error && (
            <div className="mt-3 p-2 text-xs rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
              {error}
            </div>
          )}

          {result && <ResultCard result={result} />}
        </div>
      )}
    </div>
  );
}

// ── Provider badge ─────────────────────────────────────

function ProviderBadge({ label, ok, message }: { label: string; ok: boolean; message?: string }) {
  return (
    <div
      className={`px-2 py-1.5 rounded border text-xs ${
        ok
          ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
          : 'border-slate-300 bg-slate-50 text-slate-600 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-400'
      }`}
    >
      <span className="font-medium">{label}</span>
      <span className="ml-1.5 text-[10px] uppercase tracking-wider">
        {ok ? '✓ prêt' : '○ non configuré'}
      </span>
      {!ok && message && <div className="text-[10px] opacity-70 mt-0.5">{message}</div>}
    </div>
  );
}

// ── Result card ────────────────────────────────────────

function verdictStyle(v: TrademarkRiskVerdict): { label: string; bg: string; text: string; emoji: string } {
  switch (v) {
    case 'GREEN':
      return { label: 'Voie libre', bg: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700', text: 'text-emerald-800 dark:text-emerald-300', emoji: '🟢' };
    case 'AMBER':
      return { label: 'Vigilance', bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700', text: 'text-amber-800 dark:text-amber-300', emoji: '🟡' };
    case 'RED':
      return { label: 'Conflit majeur', bg: 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700', text: 'text-red-800 dark:text-red-300', emoji: '🔴' };
    case 'UNKNOWN':
      return { label: 'Recherche non effectuée', bg: 'bg-slate-100 dark:bg-slate-800/40 border-slate-400 dark:border-slate-600', text: 'text-slate-700 dark:text-slate-300', emoji: '⚪' };
  }
}

function ResultCard({ result }: { result: TrademarkSearchResult }) {
  const v = verdictStyle(result.verdict);
  const inpi = result.sourceStatus.INPI;
  const euipo = result.sourceStatus.EUIPO;
  return (
    <div className="mt-4 space-y-3">
      <div className={`p-3 rounded-lg border ${v.bg}`}>
        <div className={`text-sm font-semibold ${v.text} flex items-center gap-2`}>
          <span>{v.emoji}</span>
          {v.label}
        </div>
        <div className={`text-xs ${v.text} mt-1`}>{result.verdictExplanation}</div>
      </div>

      {/* Per-source actual status (post-search) — shows REAL errors when an API fails. */}
      {(!inpi.ok || !euipo.ok) && (
        <div className="space-y-1.5 text-xs">
          {!inpi.ok && (
            <div className="px-2 py-1.5 rounded border border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-900/20 dark:text-red-300">
              <div className="font-medium">INPI — appel échoué</div>
              {inpi.message && <div className="opacity-80 mt-0.5 font-mono text-[10px] break-all">{inpi.message}</div>}
            </div>
          )}
          {!euipo.ok && (
            <div className="px-2 py-1.5 rounded border border-slate-300 bg-slate-50 text-slate-600 dark:border-slate-600 dark:bg-slate-800/40 dark:text-slate-400">
              <div className="font-medium">EUIPO — appel échoué</div>
              {euipo.message && <div className="opacity-80 mt-0.5 font-mono text-[10px] break-all">{euipo.message}</div>}
            </div>
          )}
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <Stat label="Total" value={result.stats.totalHits} />
        <Stat label="Exact" value={result.stats.exactHits} accent={result.stats.exactHits > 0 ? 'red' : undefined} />
        <Stat label="Quasi" value={result.stats.quasiHits} accent={result.stats.quasiHits > 0 ? 'red' : undefined} />
        <Stat label="Phonétique" value={result.stats.phoneticHits} accent={result.stats.phoneticHits > 0 ? 'amber' : undefined} />
      </div>

      {/* Hits list */}
      {result.hits.length > 0 && (
        <div className="border rounded-lg border-slate-200 dark:border-slate-700 divide-y divide-slate-200 dark:divide-slate-700 max-h-72 overflow-y-auto">
          {result.hits.map((hit, i) => (
            <HitRow key={`${hit.source}-${hit.registrationNumber}-${i}`} hit={hit} />
          ))}
        </div>
      )}

      {/* Disclaimer */}
      <p className="text-[11px] italic text-slate-500 dark:text-slate-400 shader:text-white/50 leading-relaxed">
        ⚠️ {result.disclaimer}
      </p>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: 'red' | 'amber' }) {
  const accentClass =
    accent === 'red' ? 'text-red-700 dark:text-red-300' :
    accent === 'amber' ? 'text-amber-700 dark:text-amber-300' :
    'text-slate-700 dark:text-slate-300';
  return (
    <div className="p-2 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
      <div className={`text-lg font-display ${accentClass}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  );
}

function HitRow({ hit }: { hit: TrademarkHit }) {
  const matchBadge = {
    exact: { label: 'Exact', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
    quasi: { label: 'Quasi-identique', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
    phonetic: { label: 'Phonétique', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
    substring: { label: 'Inclusion', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
    related: { label: 'Approchant', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  }[hit.matchKind];

  return (
    <div className="p-2 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 dark:text-white truncate">{hit.markName}</div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
            {hit.source} · {hit.registrationNumber || '—'}
            {hit.filingDate && ` · ${hit.filingDate.slice(0, 10)}`}
            {hit.status && ` · ${hit.status}`}
          </div>
          {hit.owner?.name && (
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              Titulaire : {hit.owner.name}{hit.owner.country ? ` (${hit.owner.country})` : ''}
            </div>
          )}
          <div className="mt-1 flex flex-wrap gap-1">
            {hit.niceClasses.map((c) => (
              <span
                key={c}
                className={`text-[10px] px-1.5 py-0.5 rounded ${
                  hit.overlappingClasses.includes(c)
                    ? 'bg-violet-600 text-white font-medium'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                }`}
                title={hit.overlappingClasses.includes(c) ? 'Classe ciblée par votre dépôt' : ''}
              >
                Cl. {c}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${matchBadge.cls}`}>
            {matchBadge.label} · {hit.similarityScore}
          </span>
          {hit.publicUrl && (
            <a
              href={hit.publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-violet-600 dark:text-violet-400 hover:underline"
            >
              Voir →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
